import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { OverviewDto } from '@models/job-request-overview-dto.interface';
import {
  AddIterationDto,
  AddBlendDto,
  AssignFormulationBarcodeDto,
  AddFormulationMaterialDto,
  CreateShippingIterationDto,
  FormulationComponentDto,
  IterationActionResultDto,
  JobRequestIterationsDto,
  SaveDesignDto,
  SetShippingVolumeDto,
  StartFromScratchDto,
  UpdateFormulationComponentSpecificGravityDto,
  UpdateFormulationSequenceDto,
} from '@models/job-request-iterations-dto.interface';

@Injectable({ providedIn: 'root' })
export class DesignService {
  private readonly api = inject(ApiService);

  getIterations(jobRequestId: number): Observable<JobRequestIterationsDto> {
    return this.api.get(`designs/job-requests/${jobRequestId}`);
  }

  createDesign(jobRequestId: number, dto: SaveDesignDto): Observable<number> {
    return this.api.post(`designs?jobRequestId=${jobRequestId}`, dto as unknown as object);
  }

  addIteration(jobRequestId: number, dto: AddIterationDto = {}): Observable<IterationActionResultDto> {
    return this.api.post(`designs/job-requests/${jobRequestId}/actions/add-iteration`, dto as unknown as object);
  }

  startFromScratch(jobRequestId: number, dto: StartFromScratchDto = {}): Observable<IterationActionResultDto> {
    return this.api.post(`designs/job-requests/${jobRequestId}/actions/start-from-scratch`, dto as unknown as object);
  }

  createShippingIteration(
    jobRequestId: number,
    dto: CreateShippingIterationDto
  ): Observable<IterationActionResultDto> {
    return this.api.post(`designs/job-requests/${jobRequestId}/actions/create-shipping`, dto as unknown as object);
  }

  ensureFirstIteration(jobRequestId: number): Observable<IterationActionResultDto> {
    return this.api.post(`designs/job-requests/${jobRequestId}/actions/ensure-first-iteration`, {});
  }

  updateDesign(id: number, dto: SaveDesignDto): Observable<void> {
    return this.api.put(`designs/${id}`, dto as unknown as object);
  }

  setShippingVolume(designId: number, dto: SetShippingVolumeDto): Observable<void> {
    return this.api.put(`designs/${designId}/shipping-volume`, dto as unknown as object);
  }

  deleteDesign(id: number): Observable<void> {
    return this.api.delete(`designs/${id}`);
  }

  addFormulationMaterial(jobRequestId: number, dto: AddFormulationMaterialDto): Observable<FormulationComponentDto> {
    return this.api.post(`designs/job-requests/${jobRequestId}/materials`, dto as unknown as object);
  }

  setMudCheckMaterial(jobRequestId: number, materialId: number): Observable<FormulationComponentDto> {
    return this.api.post(`designs/job-requests/${jobRequestId}/mud-check-material`, { materialId });
  }

  removeMudCheckMaterial(jobRequestId: number): Observable<void> {
    return this.api.delete(`designs/job-requests/${jobRequestId}/mud-check-material`);
  }

  addBlend(jobRequestId: number, dto: AddBlendDto = {}): Observable<FormulationComponentDto> {
    return this.api.post(`designs/job-requests/${jobRequestId}/blend`, dto as unknown as object);
  }

  updateFormulationSequence(jobRequestId: number, dto: UpdateFormulationSequenceDto): Observable<void> {
    return this.api.put(`designs/job-requests/${jobRequestId}/sequence`, dto as unknown as object);
  }

  assignFormulationComponentBarcode(
    jobRequestId: number,
    formulationComponentId: number,
    dto: AssignFormulationBarcodeDto
  ): Observable<FormulationComponentDto> {
    return this.api.put(
      `designs/job-requests/${jobRequestId}/formulation-components/${formulationComponentId}/barcode`,
      dto as unknown as object
    );
  }

  unassignFormulationComponentBarcode(
    jobRequestId: number,
    formulationComponentId: number
  ): Observable<FormulationComponentDto> {
    return this.api.delete(
      `designs/job-requests/${jobRequestId}/formulation-components/${formulationComponentId}/barcode`
    );
  }

  updateFormulationComponentSpecificGravity(
    jobRequestId: number,
    formulationComponentId: number,
    dto: UpdateFormulationComponentSpecificGravityDto
  ): Observable<FormulationComponentDto> {
    return this.api.put(
      `designs/job-requests/${jobRequestId}/formulation-components/${formulationComponentId}/specific-gravity`,
      dto as unknown as object
    );
  }

  removeFormulationComponent(jobRequestId: number, formulationComponentId: number): Observable<void> {
    return this.api.delete(`designs/job-requests/${jobRequestId}/formulation-components/${formulationComponentId}`);
  }

  addTestGroupToDesign(designId: number, groupName?: string): Observable<{ testGroupId: number }> {
    return this.api.post(`designs/${designId}/testgroups`, { name: groupName ?? null });
  }

  renameTestGroupInDesign(designId: number, testGroupId: number, name: string): Observable<void> {
    return this.api.put(`designs/${designId}/testgroups/${testGroupId}`, { name });
  }

  deleteTestGroupFromDesign(designId: number, testGroupId: number): Observable<void> {
    return this.api.delete(`designs/${designId}/testgroups/${testGroupId}`);
  }

  getDesignTestOverview(designId: number): Observable<OverviewDto[]> {
    return this.api.get(`designs/${designId}/test-overview`);
  }
}
